/**
 * Localisation.
 *
 * Same catalogue shape as the other Institute apps - `{ translation: { ... } }`
 * keyed by section - so a translator who has worked on one can work on this one
 * without learning a second format, and the resource files stay portable.
 *
 * The difference is what sits on top. This app has no component framework to
 * re-render on a language change, so instead of a hook there is a subscription:
 * anything that has already written text into the DOM registers a rebuild, and
 * `setLanguage` fires them all. That keeps the 3D scene, the running solver and
 * the student's place on the floor intact across a language switch, which a
 * reload would throw away.
 */

import i18next from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { initReactI18next } from 'react-i18next'

import { i18n_en } from './en'
import { i18n_es } from './es'
import { i18n_zh_cn } from './zh_cn'
import { i18n_zh_tw } from './zh_tw'

/** Resource keys, matching the sibling apps' underscored form. */
export const LANGUAGES = ['en', 'es', 'zh_cn', 'zh_tw'] as const
export type Language = (typeof LANGUAGES)[number]

/**
 * Each language named in itself. A student looking for their own language is
 * looking for the word they would use for it, not the English one.
 */
export const LANGUAGE_LABELS: Record<Language, string> = {
  en: 'English',
  es: 'Español',
  zh_cn: '简体中文',
  zh_tw: '繁體中文',
}

/**
 * Resource keys are not valid BCP-47, so the tag that goes on <html> is mapped
 * rather than reused. It matters for more than tidiness: the script subtag is
 * what makes a browser pick a Simplified rather than a Traditional face.
 */
const HTML_LANG: Record<Language, string> = {
  en: 'en',
  es: 'es',
  zh_cn: 'zh-Hans',
  zh_tw: 'zh-Hant',
}

const STORAGE_KEY = 'simfab.lang'

/** Fold whatever the browser reports onto a resource key we actually carry. */
function normalise(detected: string): Language {
  const raw = detected.toLowerCase().replace(/-/g, '_')
  if (raw.startsWith('zh')) {
    // Script subtag wins where it is present; otherwise the region decides, and
    // the Traditional regions are the shorter list.
    if (raw.includes('hant') || /_(tw|hk|mo)\b/.test(raw)) return 'zh_tw'
    return 'zh_cn'
  }
  if (raw.startsWith('es')) return 'es'
  if (raw.startsWith('en')) return 'en'
  return 'en'
}

// initReactI18next binds THIS instance to react-i18next's useTranslation.
// SimFab does not use that hook itself - its own t() is imperative - but a
// component imported from a sibling app does, and the alternative was letting
// Energy2D's i18n.ts initialise the default instance a second time, which
// would clobber these catalogues. One instance, both consumers.
void i18next.use(LanguageDetector).use(initReactI18next).init({
  fallbackLng: 'en',
  supportedLngs: LANGUAGES as unknown as string[],
  // Without this, `es_MX` would fall back to `es` only after a failed lookup of
  // a resource file that does not exist. `normalise` has already done the job.
  load: 'currentOnly',
  detection: {
    order: ['localStorage', 'navigator', 'htmlTag'],
    lookupLocalStorage: STORAGE_KEY,
    caches: [],
    convertDetectedLanguage: normalise,
  },
  interpolation: { escapeValue: false },
  resources: {
    en: i18n_en,
    es: i18n_es,
    zh_cn: i18n_zh_cn,
    zh_tw: i18n_zh_tw,
  },
})

const listeners = new Set<() => void>()

/**
 * Register something that has text on screen. Returns its own unsubscribe, so
 * a module can drop its listener when it unmounts and a stale panel never gets
 * rebuilt after the student has walked away from it.
 */
export function onLanguageChange(rebuild: () => void): () => void {
  listeners.add(rebuild)
  return () => {
    listeners.delete(rebuild)
  }
}

export function currentLanguage(): Language {
  return normalise(i18next.resolvedLanguage ?? i18next.language ?? 'en')
}

export async function setLanguage(lang: Language): Promise<void> {
  if (lang === currentLanguage()) return
  await i18next.changeLanguage(lang)
  try {
    localStorage.setItem(STORAGE_KEY, lang)
  } catch {
    /* private browsing; the choice simply does not outlive the session */
  }
  document.documentElement.lang = HTML_LANG[lang]
  for (const rebuild of [...listeners]) rebuild()
}

/**
 * Look up a string. Values interpolate with `{{name}}`, matching the sibling
 * apps. A missing key returns the key itself rather than an empty string, so a
 * gap in a catalogue shows up on screen instead of silently blanking a label.
 */
export function t(key: string, values?: Record<string, string | number>): string {
  return String(i18next.t(key, values ?? {}))
}

/**
 * Look up a list-valued key - the evidence bullets under a fault, for instance.
 * Returns an empty array rather than throwing if the key is missing or is not a
 * list, so a catalogue gap drops a bullet instead of blanking the panel.
 */
export function tList(key: string): string[] {
  const value = i18next.t(key, { returnObjects: true }) as unknown
  return Array.isArray(value) ? (value as string[]) : []
}

// Guarded because this module is reached from the physics and fault harnesses,
// which run under node with no DOM. A bare top-level DOM write here throws
// before a single assertion runs.
if (typeof document !== 'undefined') {
  document.documentElement.lang = HTML_LANG[currentLanguage()]
}

/**
 * Look up a key, falling back to a supplied string when the catalogue has no
 * entry for it.
 *
 * This exists because i18next returns the KEY when a lookup misses, which on a
 * panel renders as the literal text "recipe.preset.hotScan". A tool whose
 * strings have not been translated yet should show its English source text, not
 * its own key - a gap in the catalogue is a missing translation, not a missing
 * sentence.
 */
export function tOr(key: string, fallback: string): string {
  const value = String(i18next.t(key, {}))
  return value === key ? fallback : value
}
